import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Typography } from "@mui/material";
import LoadingButton from "../Buttons/LoadingButton";
import { usePatient } from "src/context/PatientProvider";
import {
  Coupon,
  OnlineVisit,
  ProductCategories,
  ProductPrice,
} from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { useProduct } from "src/context/ProductProvider";
import { useUpdateVisitMutation } from "src/api/services/visitApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useAfterOneTimePaymentSuccessMutation } from "src/api/services/paymentsApi";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePayment } from "src/context/PaymentProvider/usePayment";
import { useSuccessPayment } from "src/hooks/useSuccessPayment";
import PaymentValidationModal from "./components/PaymentValidationModal";
import { usePaymentSearchParams } from "src/hooks/usePaymentSearchParams";
import { Paths } from "src/constants/paths";
import { useDeterminePrescriptionType } from "src/hooks/useDeterminePrescriptionType";

const mapNumberOfMonthsToDays: Record<number, number> = {
  1: 28,
  3: 84,
};

interface PaymentFormWithVisitTypes {
  buttonText: string;
  product: ProductPrice;
  coupon: Coupon | null;
  dueToday: number;
  visit: OnlineVisit;
}

export const PaymentFormWithVisit = ({
  buttonText,
  product,
  coupon,
  dueToday,
  visit,
}: PaymentFormWithVisitTypes) => {
  const [updateVisit] = useUpdateVisitMutation();
  const { quantity, dosage, form } = usePaymentSearchParams();
  const { payWithPaymentIntent, payWithSetupIntent } = usePayment();
  const onSuccess = useSuccessPayment({
    price: product,
    couponId: coupon?.id,
  });
  const { submitAnswer } = useSubmitAnswers();

  const daysSupply = useMemo(() => {
    if (product.product_category.name === ProductCategories.WeightLoss) {
      if (
        product.display_name?.startsWith("Oral") ||
        product.name === "Metformin"
      ) {
        return 30;
      }
      return mapNumberOfMonthsToDays[product.number_of_months];
    }

    return product.interval_count;
  }, [
    product.display_name,
    product.interval_count,
    product.name,
    product.number_of_months,
    product.product_category.name,
  ]);

  const handleSuccess = useCallback(
    async ({
      invoiceId,
      paymentMethodId,
    }: {
      invoiceId?: string;
      paymentMethodId?: string;
    }) => {
      return onSuccess({
        paymentMethodId,
        callbacks: [
          () => submitAnswer(["Ignore"]),
          () =>
            updateVisit({
              slug: visit.slug,
              data: {
                status:
                  visit.visit_type === "Initial" ? undefined : "completed",
                completed_at:
                  visit.visit_type === "Initial"
                    ? undefined
                    : new Date().toISOString(),
                stripe_price_id: product.stripe_id,
                metadata: {
                  ...visit.metadata,
                  days_supply: daysSupply,
                  invoice: invoiceId,
                  dosage,
                  medication_form: form,
                  quantity: quantity != null ? Number(quantity) : undefined,
                  price: undefined,
                },
              },
            }).unwrap(),
        ],
      });
    },
    [
      daysSupply,
      dosage,
      form,
      onSuccess,
      product.stripe_id,
      quantity,
      submitAnswer,
      updateVisit,
      visit.metadata,
      visit.slug,
      visit.visit_type,
    ],
  );

  const handleContinue = useCallback(async () => {
    if (dueToday > 0) {
      const invoice = await payWithPaymentIntent();

      return handleSuccess({ invoiceId: invoice?.id });
    }

    const setupIntent = await payWithSetupIntent();

    return handleSuccess({
      paymentMethodId: setupIntent?.payment_method as string,
    });
  }, [dueToday, payWithSetupIntent, payWithPaymentIntent, handleSuccess]);

  return (
    <BasicPaymentForm
      handleContinue={handleContinue}
      buttonText={buttonText}
      handleSuccess={handleSuccess}
    />
  );
};

interface PaymentFormWithoutVisitTypes {
  buttonText: string;
  product: ProductPrice;
  coupon: Coupon | null;
  dueToday: number;
}

export const PaymentFormWithoutVisit = ({
  buttonText,
  product,
  coupon,
  dueToday,
}: PaymentFormWithoutVisitTypes) => {
  const { quantity, form, dosage } = usePaymentSearchParams();
  const navigate = useNavigate();
  const { payWithPaymentIntent, payWithSetupIntent } = usePayment();
  const [afterOneTimePaymentSuccess] = useAfterOneTimePaymentSuccessMutation();
  const onSuccess = useSuccessPayment({ price: product, couponId: coupon?.id });
  const prescriptionType = useDeterminePrescriptionType(product.product_id);

  const daysSupply = useMemo(() => {
    if (product.product_category.name === ProductCategories.WeightLoss) {
      return mapNumberOfMonthsToDays[product.number_of_months];
    }

    return product.interval_count;
  }, [
    product.interval_count,
    product.number_of_months,
    product.product_category.name,
  ]);

  const handleSuccess = useCallback(
    async ({
      invoiceId,
      paymentMethodId,
    }: {
      invoiceId?: string;
      paymentMethodId?: string;
    }) => {
      if (invoiceId != null) {
        await afterOneTimePaymentSuccess({
          product_id: product.product_id,
          product_category_id: product.product_category_id,
          billing_model: product.product.billing_model,
          dispense_quantity: quantity,
          days_supply: daysSupply,
          metadata: {
            dosage: dosage || undefined,
            invoice_id: invoiceId,
            form,
            prescription_type: prescriptionType,
          },
          approval_required: product.product.slug === "ec" ? false : true,
        }).unwrap();
      }

      return onSuccess({
        paymentMethodId,
        callbacks: [() => navigate(Paths.PATIENT_DASHBOARD, { replace: true })],
      });
    },
    [
      afterOneTimePaymentSuccess,
      daysSupply,
      dosage,
      form,
      navigate,
      onSuccess,
      prescriptionType,
      product.product.billing_model,
      product.product.slug,
      product.product_category_id,
      product.product_id,
      quantity,
    ],
  );

  const handleContinue = useCallback(async () => {
    if (dueToday > 0) {
      const invoice = await payWithPaymentIntent();

      return handleSuccess({
        invoiceId: invoice?.id,
      });
    }

    const setupIntent = await payWithSetupIntent();

    return handleSuccess({
      paymentMethodId: setupIntent?.payment_method as string,
    });
  }, [dueToday, payWithSetupIntent, handleSuccess, payWithPaymentIntent]);

  return (
    <BasicPaymentForm
      handleContinue={handleContinue}
      buttonText={buttonText}
      handleSuccess={handleSuccess}
    />
  );
};

interface BasicPaymentFormProps {
  handleSuccess: ({
    invoiceId,
    paymentMethodId,
  }: {
    invoiceId?: string;
    paymentMethodId?: string;
  }) => Promise<void>;
  handleContinue: () => Promise<void>;
  buttonText: string;
}

const BasicPaymentForm = ({
  handleSuccess,
  handleContinue,
  buttonText,
}: BasicPaymentFormProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  // const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [searchParams] = useSearchParams();
  const errorLog = useSentryLogger("error");
  const [error, setError] = useState<string | null>(null);
  const firstName = usePatient((patient) => patient.profile.first_name);
  const lastName = usePatient((patient) => patient.profile.last_name);
  const productSlug = useProduct((product) => product.slug);
  const billingModel = useProduct((product) => product.billing_model);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const component = "paymentForm";
  const page = "payment-information";

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      await handleContinue();
    } catch (err) {
      errorLog("Error handling payment", err);
      setError(
        t(
          `${page}.${component}.error`,
          "There was an error processing payment.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [errorLog, handleContinue, t]);

  const handleClose = useCallback(() => {
    setOpenVerifyModal(false);
  }, []);

  const text = useMemo(() => {
    if (["wp-ot"].includes(productSlug)) {
      return t(`${page}.${component}.${productSlug}.text`);
    }

    return t(`${page}.${component}.${billingModel}.text`);
  }, [billingModel, productSlug, t]);

  useEffect(() => {
    if (searchParams.get("redirect_status") === "succeeded") {
      setOpenVerifyModal(true);
    }
  }, [searchParams]);

  return !stripe || !elements ? (
    <Typography>{t(`${page}.${component}.loading`)}.....</Typography>
  ) : (
    <>
      <Stack gap={3} id="payment-input">
        <Stack gap={1}>
          <Typography variant={isMobile ? "body1" : "h5"} fontWeight={600}>
            {t(`${page}.${component}.header`)}
          </Typography>
          <Typography variant={isMobile ? "body2" : "body1"}>{text}</Typography>
        </Stack>

        <PaymentElement
          options={{
            terms: {
              card: "never",
              googlePay: "never",
              applePay: "never",
            },
            layout: {
              type: "accordion",
              radios: false,
              spacedAccordionItems: true,
            },
            fields: {
              billingDetails: {
                address: "if_required",
              },
            },
            defaultValues: {
              billingDetails: {
                name: `${firstName} ${lastName}`,
              },
            },
          }}
        />

        {error && (
          <Typography textAlign="center" sx={{ color: "red" }}>
            {error}
          </Typography>
        )}

        <LoadingButton
          id="payment-submit-button"
          loading={loading}
          disabled={!stripe || !elements}
          onClick={handleSubmit}
        >
          {buttonText}
        </LoadingButton>
      </Stack>
      {openVerifyModal === true ? (
        <PaymentValidationModal
          onClose={handleClose}
          onError={setError}
          handleSuccess={handleSuccess}
        />
      ) : null}
      {/* <PaymentProccessModal
        loading={loading}
        error={error}
        open={openModal}
        setOpen={setOpenModal}
        setError={setError}
        form={form}
      /> */}
    </>
  );
};
